import React, { useEffect, type FC, useState } from 'react';
import cx from 'classnames';
import { format, parse } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { Space } from '@g2u/ui-kit';
import { Helmet } from 'react-helmet-async';
import { Alert, Form, Skeleton } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import he from 'he';

import { AppointmentSelection, Content, JobConfirmation, SendNotification } from 'components';
import { useSendJobNotification } from 'helpers/hooks/useSendJobNotifications';
import { RecordLockType, ReschedulingSteps, Service, SlotAvailability, type JsonDataItem } from 'types';
import { RootStore, BookingStore } from 'store';
import { useCheckLock } from 'hooks/useCheckLock';
import { EditDetails, RescheduleActions, ReviewDetails, SelectRescheduleReason, SelectSlotSimple } from './components';
import { useRescheduleDetails } from './hooks/useRescheduleDetails';
import { useRescheduleSlot } from './hooks/useRescheduleSlot';
import styles from './reschedule.module.scss';

export const JobReschedulePage: FC = observer(() => {
  const [changedFormState, setChangedFormState] = useState<JsonDataItem>();
  const {
    technician,
    slot,
    job,
    setJob,
    setServiceSelection,
    setServiceList,
    resetBookingProgress,
    isNewCustomer,
    customer,
    location,
    setLocation,
    setTechnician,
    setSlot,
    setServiceType,
    lostJob,
    setLostJob,
    setPreviousJob,
    setTechniciansList,
    previousJob,
    productList,
    productSelection,
    serviceType,
    isAvailabilityOverrideEnabled,
    setIsAvailabilityOverrideEnabled,
  } = BookingStore;
  const [isProductsLoading, setIsProductsLoading] = useState<boolean>(false);
  const { setBreadcrumbs } = RootStore;
  const navigate = useNavigate();
  const { id, step } = useParams();
  const showAlert =
    [
      ReschedulingSteps.ReviewDetails,
      ReschedulingSteps.EditDetails,
      ReschedulingSteps.SelectSlot,
      ReschedulingSteps.SelectSlotSimple,
    ].includes(step as ReschedulingSteps) && job;
  const { isLocked } = useCheckLock({ recordId: id ? Number(id) : undefined, recordLockType: RecordLockType.Job });

  // Set initial data
  const {
    serviceList,
    jobData,
    serviceSelection,
    rescheduleReasons,
    selectedReason,
    setSelectedReason,
    updateServiceSelection,
    isDataLoading,
  } = useRescheduleDetails(id);

  const [editDetailsForm] = Form.useForm();
  useEffect(() => {
    jobData && setJob(jobData);
    serviceList && setServiceList(serviceList);
    serviceSelection && setServiceSelection(serviceSelection);
  }, [serviceList, jobData, serviceSelection]);

  // Slot Selection
  const handleSlotFormChange = (changed: JsonDataItem) => {
    setChangedFormState(changed);
  };
  const { slotForm, rescheduledJobData, isTechConfirmed, setIsTechConfirmed, rescheduleJob, isAppointmentUpdating } =
    useRescheduleSlot(job, slot, technician, selectedReason, isAvailabilityOverrideEnabled, serviceSelection);
  useEffect(() => {
    rescheduledJobData && setJob(rescheduledJobData);
  }, [rescheduledJobData]);

  // Send notification
  const [notificationForm] = Form.useForm();
  const {
    sendNotifications,
    isNotifying,
    initialValues,
    emailOptions,
    smsOptions,
    isNotified: isNotificationSent,
  } = useSendJobNotification(job, technician, SendNotification.Type.Reschedule);

  // Restart rescheduling after confirmation TOTDO
  const handleRestartRescheduling = () => {
    setSelectedReason(null);
    navigate(`/job/reschedule/${id}/${ReschedulingSteps.SelectReason}`);
  };

  useEffect(() => {
    setBreadcrumbs([{ url: '/job/search', title: 'Find a job' }, { title: 'Reschedule' }]);

    return () => {
      resetBookingProgress();
      setBreadcrumbs(null);
    };
  }, []);

  useEffect(() => {
    // reset progress on page refresh, because selected reason saved only in memory
    navigate(`/job/reschedule/${id}/${ReschedulingSteps.SelectReason}`);
  }, []);

  useEffect(() => {
    isNotificationSent && navigate(`/job/reschedule/${id}/${ReschedulingSteps.ReviewSummary}`);
  }, [isNotificationSent]);

  useEffect(() => {
    if (
      step === ReschedulingSteps.SendNotification &&
      rescheduleReasons?.find(({ ID }) => ID === selectedReason)?.category === 'R'
    ) {
      navigate(`/job/edit/${id}/summary`);
    }
  }, [step, rescheduleReasons, selectedReason]);

  if (!job || !id) {
    return (
      <Space.$ fullWidth justify={Space.Justify.Center} align={Space.Align.Center}>
        <Skeleton />
      </Space.$>
    );
  }

  return (
    <Space.$ fullWidth direction={Space.Direction.Vertical}>
      {showAlert && (
        <Alert
          type="warning"
          showIcon
          message={
            <>
              You are rescheduling Appointment{' '}
              <b>
                {job && job.products[0]
                  ? format(
                      parse(job.products[0].service_technicians[0].appointment, 'yyyy-MM-dd HH:mm:ss', new Date()),
                      'h:mma dd/MM/yyyy',
                    )
                  : ''}
              </b>{' '}
              | Tech <b>{he.decode(job.products[0]?.service_technicians[0].technician.name || '')}</b> for Customer{' '}
              <b>{job.customer.name.full_name}</b>
            </>
          }
          style={{ position: 'sticky', top: 46, zIndex: 2 }}
        />
      )}
      <Content.$
        dataTestId="job-rescheduling-page"
        className={cx(styles.reschedule, step === ReschedulingSteps.ReviewSummary && styles.reschedule_confirmation)}
      >
        <Helmet>
          <title>Reschedule a job</title>
        </Helmet>

        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large}>
          {step === ReschedulingSteps.SelectReason && (
            <SelectRescheduleReason.$
              disabled={isLocked}
              selectedReason={selectedReason}
              reasons={rescheduleReasons}
              isReasonsLoading={isDataLoading}
              onSelect={(id) => {
                setSelectedReason(id);
              }}
            />
          )}

          {step === ReschedulingSteps.ReviewDetails && (
            <ReviewDetails.$
              onConfirm={() => navigate(`/job/reschedule/${id}/${ReschedulingSteps.SelectSlot}`)}
              onEdit={() => navigate(`/job/reschedule/${id}/${ReschedulingSteps.EditDetails}`)}
            />
          )}

          {step === ReschedulingSteps.EditDetails && (
            <EditDetails.$ form={editDetailsForm} onUpdate={updateServiceSelection} onLoading={setIsProductsLoading} />
          )}

          {step === ReschedulingSteps.SelectSlotSimple && (
            <SelectSlotSimple.$
              setSlot={({ day, time }: { day: string; time: string }) => {
                setSlot({
                  day,
                  time,
                  // Set some defaults that aren't relevant for simple rescheduling
                  techId: '',
                  availability: SlotAvailability.Available,
                  isSecondaryArea: false,
                  isSurrounding: false,
                });
                setTechnician(job.products[0]?.service_technicians[0].technician);
              }}
              technicianId={job.products[0]?.service_technicians[0].technician?.ID}
            />
          )}

          {step === ReschedulingSteps.SelectSlot && (
            <Form form={slotForm} colon={false} layout="horizontal" onValuesChange={handleSlotFormChange}>
              <AppointmentSelection.$
                timezone={job.customer.timezone}
                isRescheduling={true}
                serviceId={id}
                form={slotForm}
                isConfirmed={isTechConfirmed}
                isAvailabilityOverrideEnabled={isAvailabilityOverrideEnabled}
                onAvailabilityOverrideChange={setIsAvailabilityOverrideEnabled}
                onConfirm={(status: boolean) => {
                  setIsTechConfirmed(status);
                }}
                changedFields={changedFormState}
                {...{
                  isNewCustomer,
                  customer,
                  location,
                  setLocation,
                  setTechnician,
                  technician,
                  setSlot,
                  slot,
                  job,
                  serviceSelection,
                  setServiceType,
                  lostJob,
                  setLostJob,
                  resetBookingProgress,
                  setPreviousJob,
                  setTechniciansList,
                  previousJob,
                  productList,
                  productSelection,
                  serviceType,
                }}
              />
            </Form>
          )}

          {step === ReschedulingSteps.SendNotification && (
            <Form
              form={notificationForm}
              layout="horizontal"
              onFinish={sendNotifications}
              colon={false}
              className={styles.reschedule__notification}
            >
              <SendNotification.$
                isStandalone={false}
                isLoading={isNotifying}
                initialValues={initialValues}
                emailOptions={emailOptions}
                smsOptions={smsOptions}
              />
            </Form>
          )}

          {step === ReschedulingSteps.ReviewSummary && (
            <JobConfirmation.$
              job={job as Service}
              openedKeys={['appointments']}
              onEdit={() => {
                handleRestartRescheduling();
              }}
            />
          )}

          <RescheduleActions.$
            jobId={id}
            step={step as ReschedulingSteps}
            disabled={!selectedReason || (step === ReschedulingSteps.SelectSlot && !isTechConfirmed)}
            selectedReason={rescheduleReasons?.find(({ ID }) => ID === selectedReason)}
            isLoading={isDataLoading || isAppointmentUpdating || isNotifying || isProductsLoading}
            onReschedule={rescheduleJob}
            onNotify={() => notificationForm.submit()}
            onUpdate={() => editDetailsForm.submit()}
          />
        </Space.$>
      </Content.$>
    </Space.$>
  );
});
